import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import { api, clearToken, getToken, setToken } from "../lib/api";
import type { AppRole, CurrentUser } from "../lib/types";

export type Surface = "shiftflow" | "crm";

interface AuthState {
    user: CurrentUser | null;
    loading: boolean;
    signIn: (email: string, password: string) => Promise<void>;
    signOut: () => void;
    /** True when the role is at or above the one given, on the workforce ladder. */
    atLeast: (role: AppRole) => boolean;
    /** Which of the two application surfaces the user is currently in. */
    surface: Surface;
    setSurface: (surface: Surface) => void;
    /** Whether the account is allowed into a surface at all. */
    mayAccess: (surface: Surface) => boolean;
    /**
     * Whether the account may do one specific quality-module thing, as the
     * server resolved it (role defaults + overrides). Admin is always yes;
     * an account from before the map existed falls back to no.
     */
    mayQuality: (key: string) => boolean;
}

const RANK: Record<AppRole, number> = { admin: 5, manager: 4, supervisor: 3, staff: 2, viewer: 1, partner: 0, support: 0 };

const SURFACE_KEY = "trustcare.surface";

const AuthContext = createContext<AuthState | null>(null);

/** The surface a freshly signed-in account lands on. */
const defaultSurfaceFor = (user: CurrentUser): Surface =>
    user.canAccessShiftflow ? "shiftflow" : "crm";

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [user, setUser] = useState<CurrentUser | null>(null);
    const [loading, setLoading] = useState(true);
    const [surface, setSurfaceState] = useState<Surface>(
        () => (localStorage.getItem(SURFACE_KEY) as Surface) || "shiftflow"
    );

    const setSurface = useCallback((next: Surface) => {
        localStorage.setItem(SURFACE_KEY, next);
        setSurfaceState(next);
    }, []);

    const signOut = useCallback(() => {
        clearToken();
        localStorage.removeItem(SURFACE_KEY);
        setUser(null);
    }, []);

    useEffect(() => {
        // The API dispatches this when a request comes back 401, so a revoked
        // account is ejected immediately rather than on next navigation.
        const onSignedOut = () => setUser(null);
        window.addEventListener("trustcare:signed-out", onSignedOut);
        return () => window.removeEventListener("trustcare:signed-out", onSignedOut);
    }, []);

    useEffect(() => {
        if (!getToken()) {
            setLoading(false);
            return;
        }
        api.get<CurrentUser>("/auth/me")
            .then((me) => {
                setUser(me);
                // A stored surface the account can no longer enter is corrected
                // silently — a revoked grant must not strand someone on a
                // surface that will refuse every request.
                const stored = localStorage.getItem(SURFACE_KEY) as Surface | null;
                const allowed = (s: Surface) => (s === "crm" ? me.canAccessCrm : me.canAccessShiftflow);
                if (!stored || !allowed(stored)) {
                    setSurface(defaultSurfaceFor(me));
                }
            })
            .catch(() => clearToken())
            .finally(() => setLoading(false));
    }, [setSurface]);

    const signIn = useCallback(async (email: string, password: string) => {
        const result = await api.post<{ token: string; user: CurrentUser }>("/auth/login", {
            email,
            password,
        });
        setToken(result.token);
        setUser(result.user);
        setSurface(defaultSurfaceFor(result.user));
    }, [setSurface]);

    const value = useMemo<AuthState>(
        () => ({
            user,
            loading,
            signIn,
            signOut,
            atLeast: (role) => (user ? RANK[user.role] >= RANK[role] : false),
            surface,
            setSurface,
            mayAccess: (s) =>
                user ? (s === "crm" ? user.canAccessCrm : user.canAccessShiftflow) : false,
            mayQuality: (key) =>
                user ? user.role === "admin" || user.quality?.[key] === true : false,
        }),
        [user, loading, signIn, signOut, surface, setSurface]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthState => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used inside AuthProvider");
    }
    return context;
};
